import parse, { domToReact, Element, type DOMNode } from 'html-react-parser';
import { renderTextWithFootnotesReferences } from './feature-service-types';
import { sanitize } from '@jill64/universal-sanitizer';
import React from 'react';
import { TextList, TextListItem, TextLink } from '@oneaudi/unified-web-components';

import { APP_ID, NODE_ENVIRONMENT } from '../environment';

export const isDarkTheme = (theme: { name: string }): boolean => theme.name === 'dark';
export const APP_NAME = APP_ID.replace('fa-', '').replace(/-/g, ' ');

export const enum QueryParam {
  Test = 't',
  Debug = 'd_fa',
}

export const getParam = (paramName: string): string | null => {
  try {
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search.substr(1)).get(paramName);
    }
  } catch {
    return null;
  }
  return null;
};

export const isTestMode = (): boolean => getParam(QueryParam.Test) === 'true';

export const isDevMode = NODE_ENVIRONMENT === 'development';

export const isDebugMode = (): boolean => isDevMode || getParam(QueryParam.Debug) === 'true';

function replaceHtmlWithDesignSystem(domNode: DOMNode): React.JSX.Element | undefined {
  if (domNode instanceof Element) {
    const children = domNode.children as DOMNode[];

    if (domNode.name === 'ul') {
      return (
        <TextList variant="bullet" size="md" margin={['var(--spacing-relative-md)', 0, 0, 0]}>
          {domToReact(children, { replace: replaceHtmlWithDesignSystem })}
        </TextList>
      );
    }

    if (domNode.name === 'ol') {
      return (
        <TextList variant="decimal" size="md" margin={['var(--spacing-relative-md)', 0, 0, 0]}>
          {domToReact(children, { replace: replaceHtmlWithDesignSystem })}
        </TextList>
      );
    }

    if (domNode.name === 'li') {
      return (
        <TextListItem>
          {domToReact(children, { replace: replaceHtmlWithDesignSystem })}
        </TextListItem>
      );
    }

    if (domNode.name === 'a') {
      const href = domNode.attribs?.href || '';
      const target = domNode.attribs?.target;
      return (
        <TextLink
          variant="inline"
          size="md"
          href={href}
          target={target}
          margin={['var(--spacing-relative-md)', 0, 0, 0]}
        >
          {domToReact(children, { replace: replaceHtmlWithDesignSystem })}
        </TextLink>
      );
    }
  }
  return undefined;
}

export function convertRichTextWithFootnotes(htmlString: string) {
  const parsed = parse(sanitize(htmlString, {}), {
    replace: replaceHtmlWithDesignSystem,
  });
  const parsedWithNotes = renderTextWithFootnotesReferences(parsed as string);
  return parsedWithNotes;
}
